import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Lock, ChevronLeft, ChevronRight } from 'lucide-react';
import { UnifiedHintRenderer } from './HintRenderers';
import { HintData, StockData } from '../../types/game';
import { StockCategoryFilter, CategoryHelpers } from '../../types/stockCategories';
import { getShortLabel } from '../../config/hintConfig';
import { StepperTracker } from '../ui/StepperTracker';
import { useToastNotification } from '../../contexts/ToastContext';

interface HintsSectionProps {
  hints: HintData[];
  visibleHints: number;
  stockData: StockData | null;
  category: StockCategoryFilter;
  isStreamerMode?: boolean;
}

export const HintsSection: React.FC<HintsSectionProps> = ({
  hints,
  visibleHints,
  stockData,
  category,
  isStreamerMode = false,
}) => {
  const { showToast } = useToastNotification();
  const totalSteps = hints.length;
  const clampedVisible = Math.max(0, Math.min(visibleHints, totalSteps));

  // Auto-advance to the most recently unlocked hint when visibleHints increases
  const [activeStepIndex, setActiveStepIndex] = useState(() =>
    clampedVisible > 0 ? clampedVisible - 1 : 0
  );
  const prevVisibleRef = useRef(clampedVisible);
  const [justRevealedIndex, setJustRevealedIndex] = useState<number | null>(null);
  const revealClearRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const prev = prevVisibleRef.current;
    if (clampedVisible > prev && clampedVisible > 0) {
      const newIndex = clampedVisible - 1;
      setActiveStepIndex(newIndex);

      // Set reveal animation immediately — hint-reveal starts at opacity 0,
      // so new content is hidden on first paint and animates in
      clearTimeout(revealClearRef.current);
      setJustRevealedIndex(newIndex);
      revealClearRef.current = setTimeout(() => setJustRevealedIndex(null), 900);
    } else if (clampedVisible < prev && activeStepIndex >= clampedVisible && clampedVisible > 0) {
      setActiveStepIndex(clampedVisible - 1);
    }
    if (clampedVisible !== prev) {
      prevVisibleRef.current = clampedVisible;
    }
  }, [clampedVisible, activeStepIndex]);

  const categoryConfig = useMemo(
    () => (category ? CategoryHelpers.getConfig(category) : null),
    [category]
  );

  const shortLabels = useMemo(
    () => hints.map((hint) => getShortLabel(hint.title)),
    [hints]
  );

  const handleStepClick = useCallback(
    (index: number) => {
      if (index < clampedVisible) {
        setActiveStepIndex(index);
      }
    },
    [clampedVisible]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number) => {
      let nextIndex: number | null = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        // Find next unlocked step
        for (let i = index + 1; i < totalSteps; i++) {
          if (i < clampedVisible) { nextIndex = i; break; }
        }
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        for (let i = index - 1; i >= 0; i--) {
          if (i < clampedVisible) { nextIndex = i; break; }
        }
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = Math.max(0, clampedVisible - 1);
      }
      if (nextIndex !== null && nextIndex < clampedVisible) {
        setActiveStepIndex(nextIndex);
        const btn = document.getElementById(`hint-step-${nextIndex}`);
        btn?.focus();
      }
    },
    [totalSteps, clampedVisible]
  );

  const currentHint = hints[activeStepIndex];
  const isActiveUnlocked = activeStepIndex < clampedVisible;

  return (
    <div className="w-full animate-fade-up-delay" style={{ opacity: 0 }} role="region" aria-label="Game hints">
      {/* Header */}
      {!isStreamerMode && (
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-[clamp(1.25rem,2.5vw,1.625rem)] font-normal" style={{ color: 'var(--clue-text-primary)' }}>
            Company Hints
          </h3>
          <span
            className="font-mono text-[clamp(0.625rem,1.2vw,0.75rem)] font-medium px-3 py-1 rounded-full"
            style={{ color: 'var(--clue-text-tertiary)', backgroundColor: 'var(--clue-bg-secondary)' }}
          >
            {clampedVisible} of {totalSteps}
          </span>
        </div>
      )}

      {/* Stepper Track */}
      {!isStreamerMode && (
        <StepperTracker
          steps={shortLabels.map(label => ({ label }))}
          activeStepIndex={activeStepIndex}
          visibleCount={clampedVisible}
          onStepClick={handleStepClick}
          onLockedStepClick={() => showToast('Make an incorrect guess and a new hint will be revealed!')}
          onStepKeyDown={handleKeyDown}
          stepIdPrefix="hint-step"
          ariaLabel="Hint steps"
          interactive={true}
        />
      )}

      {/* Announcement region */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {isActiveUnlocked && currentHint
          ? `Viewing hint ${activeStepIndex + 1} of ${totalSteps}: ${currentHint.title}`
          : `Hint ${activeStepIndex + 1} is locked. Make a guess to unlock.`}
      </div>

      {/* Hint Card */}
      {(() => {
        const hintContent = (
          <div role="tabpanel" aria-labelledby={`hint-step-${activeStepIndex}`} className={isStreamerMode ? 'flex-1 min-w-0' : undefined}>
            {isActiveUnlocked && currentHint ? (
              <UnlockedHintCard
                hint={currentHint}
                stockData={stockData}
                categoryColor={categoryConfig?.color ?? '#3B82F6'}
                isNewlyRevealed={activeStepIndex === justRevealedIndex}
              />
            ) : (
              <LockedHintCard />
            )}
          </div>
        );

        if (!isStreamerMode) return hintContent;

        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous hint"
              disabled={activeStepIndex <= 0}
              onClick={() => handleStepClick(activeStepIndex - 1)}
              className="p-0.5 rounded transition-colors disabled:opacity-30 flex-shrink-0"
              style={{ color: 'var(--clue-text-secondary)' }}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            {hintContent}
            <button
              type="button"
              aria-label="Next hint"
              disabled={activeStepIndex >= clampedVisible - 1}
              onClick={() => handleStepClick(activeStepIndex + 1)}
              className="p-0.5 rounded transition-colors disabled:opacity-30 flex-shrink-0"
              style={{ color: 'var(--clue-text-secondary)' }}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        );
      })()}
    </div>
  );
};

/* ─── Sub-components ─── */

const UnlockedHintCard: React.FC<{
  hint: HintData;
  stockData: StockData | null;
  categoryColor: string;
  isNewlyRevealed?: boolean;
}> = ({ hint, stockData, categoryColor, isNewlyRevealed }) => {
  return (
    <div
      className={`relative rounded-2xl overflow-hidden${isNewlyRevealed ? ' animate-hint-reveal' : ''}`}
      style={{
        backgroundColor: 'var(--clue-bg-card)',
        border: '1px solid var(--clue-border-light)',
        boxShadow: 'var(--clue-shadow-sm)',
      }}
    >
      {/* Teal top border */}
      <div
        className="h-[3px] w-full"
        style={{ backgroundColor: 'var(--clue-teal-accent)' }}
      />
      <div style={{ padding: '22px 24px' }}>
        <UnifiedHintRenderer
          hint={hint}
          stockData={stockData}
          categoryColor={categoryColor}
        />
      </div>
    </div>
  );
};

const LockedHintCard: React.FC = () => {
  return (
    <div
      className="rounded-2xl flex flex-col items-center justify-center py-10 px-6 text-center"
      style={{
        border: '2px dashed var(--clue-border-mid)',
        backgroundColor: 'var(--clue-bg-locked)',
      }}
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
        style={{ backgroundColor: 'var(--clue-bg-secondary)' }}
      >
        <Lock className="w-5 h-5" style={{ color: 'var(--clue-text-locked)' }} />
      </div>
      <p className="font-serif text-[clamp(0.9375rem,1.8vw,1.125rem)] font-normal mb-1" style={{ color: 'var(--clue-text-primary)' }}>
        Hint Locked
      </p>
      <p className="text-[clamp(0.75rem,1.4vw,0.875rem)]" style={{ color: 'var(--clue-text-secondary)' }}>
        Make a guess to unlock the next hint
      </p>
    </div>
  );
};

export default HintsSection;
